import { Icon, Pill } from '@idexx-laboratories/spot-react';
import { observer } from 'mobx-react';
import React from 'react';
import { GridRenderer, GridRendererProps } from '../../components/Grid/Grid';
import { AuditEvent } from '../../types/practice-types';
import { copyToClipboard, isDefined } from '../../utils/object-utils';

const EVENT_FIELD_DISPLAY_SIZE = 100;

export const SuccessRenderer = observer((props: GridRendererProps<AuditEvent>) => {
  const { row } = props;
  switch (row.success) {
    case true:
      return (
        <Pill
          color='positive'
          size='small'
          variant='outlined'
          iconLeft='checkmark'
          label='Success'
        />
      );
    case false:
      return (
        <Pill
          color='negative'
          size='small'
          variant='outlined'
          iconLeft='cancel'
          label='Failure'
        ></Pill>
      );
    default:
      return null;
  }
});

export const displayEventField = (value: string, name: string): React.JSX.Element => {
  let truncatedValue = value || '';
  truncatedValue =
    truncatedValue.length > EVENT_FIELD_DISPLAY_SIZE
      ? `${truncatedValue.substring(0, EVENT_FIELD_DISPLAY_SIZE)} ...`
      : truncatedValue;
  return (
    <>
      <Icon
        role='icon'
        className='copy-to-clipboard-icon'
        iconName='copy'
        aria-label={`Copy ${name} To Clipboard`}
        title={`Copy ${name} To Clipboard`}
        onClick={() => copyToClipboard(value || '')}
      />
      {name}: <strong>{truncatedValue}</strong>
    </>
  );
};

const eventFields = [
  ['type', 'Type'],
  ['event', 'Event'],
  ['userEmail', 'User'],
  ['userRole', 'User Role(s)'],
  ['contactName', 'Contact Name'],
  ['contactPhone', 'Contact Phone'],
  ['agentName', 'Machine Name'],
  ['requestUrl', 'Request URL'],
  ['request', 'Request'],
  ['response', 'Response'],
  ['managementUserEmail', 'Managed User Email'],
  ['managementUserRoles', 'Managed User Role(s)'],
  ['managementUserFirstName', 'Managed User First Name'],
  ['managementUserLastName', 'Managed User Last Name'],
  ['managementUserPhone', 'Managed User Phone'],
];

export const SubRowRenderer = (props: GridRendererProps<AuditEvent>): React.JSX.Element => (
  <div className='event-details'>
    <ul>
      {eventFields
        .filter(([key]: string[]) => isDefined(props.row[key]))
        .map(([key, description]: string[], index) => (
          <li key={index}>{displayEventField(props.row[key], description)}</li>
        ))}
    </ul>
  </div>
);

interface ExpandCollapseProps extends GridRendererProps<AuditEvent> {
  action: (row: any, index: any) => void;
}

const ExpandCollapseRenderer = observer((props: ExpandCollapseProps) => {
  const { row, action, expandedRowIds, index } = props;
  const isExpanded = expandedRowIds?.includes(row.id);
  const iconPath = isExpanded ? 'caret-up' : 'caret-down';
  const iconHint = isExpanded ? 'Collapse' : 'Expand';
  return (
    <div className='expand-collapse-cell'>
      <Icon
        role='expand-collapse-icon'
        className='expand-collapse-icon'
        iconName={iconPath}
        aria-label={`Audit ${iconHint}`}
        title={iconHint}
        onClick={() => action(row, index)}
      />
    </div>
  );
});

export const expandCollapseRenderer =
  (action: (row: AuditEvent) => void): GridRenderer<AuditEvent> =>
  (props: GridRendererProps<AuditEvent>) => (
    <ExpandCollapseRenderer
      row={props.row}
      index={props.index}
      expandedRowIds={props.expandedRowIds}
      action={action}
    />
  );
